/**
 * FeatureFlagsContext - Global feature flag management system
 *
 * Provides a streamlined feature flag system with phase-based controls,
 * localStorage persistence, and provider-specific logic. This context enables
 * app-wide conditional feature enabling with granular phase management.
 */

import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useAuth } from './AuthContext'

// Core feature flag type
type FeatureFlag = {
  id: string
  name: string
  description: string
  enabled: boolean
}

// Feature flags context interface
type FeatureFlagsContextType = {
  flags: FeatureFlag[]
  isFeatureEnabled: (flagId: string) => boolean
  toggleFeature: (flagId: string) => void
  setFeatureEnabled: (flagId: string, enabled: boolean) => void
  resetToDefaults: () => void
  // Phase convenience methods (future phases only)
  isPhaseFuture: () => boolean
  // UI-specific methods
  isMotionEnabled: () => boolean
  // Development mode method
  isDevelopmentMode: () => boolean
  // Hierarchical Organization method
  isHierarchicalOrgEnabled: () => boolean
  // ReactQuery Dev Tools method
  isReactQueryDevToolsEnabled: () => boolean
  // Business Impact Import/Export method
  isBizImpactImportExport: () => boolean
  // Onboarding Wizard method
  hasOnboardingWizard: () => boolean
  // GCP connection onboarding on Cloud Services
  hasGcpOnboarding: () => boolean
  // Fixture-data preview of the GCP surfaces, for review without GCP
  hasGcpSurfacesDemo: () => boolean
  // Financial Exposure widget on main dashboard
  hasFinancialExposure: () => boolean
  // Business Impact columns and BI across the app
  hasBusinessImpact: () => boolean
  // Ripple Viewer tab in Application drawer panels
  hasRippleViewer: () => boolean
  // Global time-based filter control in the header
  hasGlobalTimeFilter: () => boolean
}

// Context creation
const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined,
)

// Custom hook
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext)
  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider',
    )
  }
  return context
}

// Default feature flags - only flags that are actively used in the application
export const AVAILABLE_FEATURE_FLAGS: FeatureFlag[] = [
  // Rule Manager (M1)
  {
    id: 'rule-manager',
    name: 'Rule Manager',
    description:
      'OOB detection rules visibility and per-tenant enable/disable control (M1)',
    enabled: false,
  },

  {
    id: 'org-hierarchy',
    name: 'Organization Hierarchy',
    description: 'Multi-level organizational structure support',
    enabled: false,
  },

  // Page Features
  {
    id: 'show-dashboard',
    name: 'Dashboard',
    description: 'Show the main Dashboard page in the sidebar navigation',
    enabled: false,
  },

  {
    id: 'show-crowdstrike-integration',
    name: 'CrowdStrike Integration Page',
    description:
      'Show the CrowdStrike Integration dashboard in the sidebar navigation (Integrations section). Admin-only even when enabled — the page itself is administrator-gated server-side; this flag adds a UI gate on top so the entry can be hidden from the sidebar without changing the role check.',
    enabled: false,
  },

  {
    id: 'essential-interface-motion-only',
    name: 'Essential Interface Motion Only',
    description:
      'Disable unnecessary animations and transitions for accessibility and performance (keeps essential drawer animations)',
    enabled: true,
  },

  // Feature Flags (experimental and optional features)
  {
    id: 'use-onboarding-wizard',
    name: 'Use Onboarding Wizard',
    description: 'Enable the onboarding wizard for new cloud account setup.',
    enabled: false,
  },
  {
    id: 'gcp-onboarding',
    name: 'GCP Onboarding',
    description:
      'Connect GCP organizations or projects from Cloud Services. Needs the GCP onboarding Lambda, which only development has today.',
    enabled: false,
  },
  {
    id: 'biz-impact-import-export',
    name: 'Business Impact Import/Export',
    description:
      'Enable import/export buttons on Business Impact Administration page.',
    enabled: false,
  },
  {
    id: 'financial-exposure',
    name: 'Financial Exposure',
    description: 'Show the financial exposure widget on the main dashboard.',
    enabled: false,
  },
  {
    id: 'business-impact',
    name: 'Business Impact',
    description: 'Show business impact columns and BI features across the app.',
    enabled: false,
  },
  {
    id: 'ripple-viewer',
    name: 'Ripple Viewer',
    description:
      'Show the Ripple Viewer graph tab on the Sites, Endpoints, and Images drawer panels in the Application dashboard.',
    enabled: false,
  },
  // AI Workloads nav flags — each gates a sidebar dashboard
  {
    id: 'ai-identities',
    name: 'AI: Identities',
    description:
      'Show the AI Identities dashboard in the sidebar — non-human identity inventory for AI workloads.',
    enabled: true,
  },
  {
    id: 'ai-compliance',
    name: 'AI: Compliance',
    description:
      'Show the Compliance dashboard in the sidebar — framework coverage and control gap analysis.',
    enabled: false,
  },
  {
    id: 'ai-scorecard',
    name: 'AI: Scorecard',
    description:
      'Show the CISO Scorecard dashboard in the sidebar — population-level NHI risk metrics and trends.',
    enabled: false,
  },

  // AI Identities — element-level gates. Each hides one in-progress element
  // that is incomplete, non-functional, or blank, so QA and customers don't
  // hit dead ends. All off by default; enable per element as its data lands.
  {
    id: 'ai-identities-runtime-usage-tab',
    name: 'AI: Identities — Runtime Usage Tab',
    description:
      'Show the Runtime Usage tab on the AI Identities page. Hidden by default until runtime-usage data is ready.',
    enabled: false,
  },
  {
    id: 'ai-identities-path-tab',
    name: 'AI: Identities — Identity Path Tab',
    description:
      'Show the Identity Path tab on the AI Identities page. Hidden by default until identity-path data is ready.',
    enabled: false,
  },
  {
    id: 'ai-identities-agents-tile',
    name: 'AI: Identities — AI Agents Tile',
    description:
      'Show the "AI Agents" summary tile on the AI Identities page header. Hidden by default until the metric is finalized.',
    enabled: false,
  },
  {
    id: 'ai-identities-external-creds-tile',
    name: 'AI: Identities — External AI Credentials Tile',
    description:
      'Show the "External AI Credentials" summary tile on the AI Identities page header. Hidden by default until these are collected as identities.',
    enabled: false,
  },
  {
    id: 'ai-identities-blast-radius-tile',
    name: 'AI: Identities — Critical Blast Radius Tile',
    description:
      'Show the "Critical Blast Radius" summary tile on the AI Identities page header. Hidden by default until reach data is available.',
    enabled: false,
  },
  {
    id: 'ai-identities-security-issues-tab',
    name: 'AI: Identities — Security Issues Drawer Tab',
    description:
      'Show the Security Issues tab in the identity detail drawer. Hidden by default until findings coverage is ready.',
    enabled: false,
  },
  {
    id: 'ai-identities-tags-tab',
    name: 'AI: Identities — Tags & Metadata Drawer Tab',
    description:
      'Show the Tags & Metadata tab in the identity detail drawer. Hidden by default until tag/metadata data is ready.',
    enabled: false,
  },
  // Identity detail-drawer tabs whose data is already live but which were
  // gated on flags referenced in code yet never registered here (so they were
  // permanently off). Registered so they can be toggled as their data lands.
  {
    id: 'ai-identity-classes',
    name: 'AI: Identities — Classification & Display Name',
    description:
      'Show the Classification section and human display-name editor in the identity detail drawer Overview tab.',
    enabled: false,
  },
  {
    id: 'ai-standing-privilege',
    name: 'AI: Identities — Standing Privilege Columns',
    description:
      'Show Standing Privilege and Max Unused Permission Level columns on the AI Identities grid, plus the Standing Privilege row on the identity detail drawer Overview tab.',
    enabled: false,
  },
  {
    id: 'ai-identities-runtime-bind',
    name: 'AI: Identities — Runtime Bind Columns',
    description:
      'Show the new Machine-table columns on the AI Identities grid (Assumed Via, Liveness, Credential model, and the Granted/Used roll-up). Hidden by default until sensor-witnessed bind and entitlement data lands.',
    enabled: false,
  },
  {
    id: 'ai-identities-risk-ranking',
    name: 'AI: Identities — Runtime Risk Ranking (sample)',
    description:
      'Replace the Identities tab grid with the runtime-identity risk-ranking surface (risk-summary cards, type strip, ranked list, and per-identity risk detail with execution chains). Renders sample data; leave off to keep the real-API inventory grid.',
    enabled: false,
  },

  // Show drill-in drawer links on AI Application detail cards
  // (excludes Context, Ownership, and Blast Radius which are always visible)
  {
    id: 'ai-drill-in-links',
    name: 'AI: Show Drill-In Links',
    description:
      'Show the drill-in drawer buttons (Runtime, Identities, Permissions, Data, Governance, Development) on AI Application detail cards.',
    enabled: false,
  },

  // AI Security nav flags — each gates a sidebar dashboard
  {
    id: 'ai-security-findings',
    name: 'AI: Security Findings',
    description:
      'Show the Security Findings dashboard in the sidebar — runtime incidents and detected events.',
    enabled: true,
  },
  {
    id: 'ai-security-posture',
    name: 'AI: Security Posture',
    description:
      'Show the Security Posture dashboard in the sidebar — vulnerabilities, misconfigurations, and sensitive data.',
    enabled: true,
  },
  {
    id: 'ai-app-grouping',
    name: 'AI: Group Applications by business unit',
    description:
      "Show the Group by control and the per-dimension filters (Business Unit, Product Line, Cost Center) on the AI Applications catalog, and the matching assignment rows in an application's Ownership drawer.",
    enabled: false,
  },
  {
    id: 'ai-trends',
    name: 'AI: Show Trends Page',
    description:
      'Show the Trends tab on the AI Application detail page — time-series charts for token volume, incident rate, and posture drift.',
    enabled: false,
  },
  {
    id: 'ai-graph-scope-modes',
    name: 'AI: Show Observed/Permitted toggle and scope in graphs.',
    description:
      'Show the Scope dropdown and the Observed (24h) / Policy-permitted segmented toggle above the Workload Paths blast-radius graph on the AI Application detail page.',
    enabled: false,
  },
  {
    id: 'ai-notification-settings-tab',
    name: 'AI: Notifications — Settings tab',
    description:
      'Show the "Notifications" tab on the Settings page, giving each user per-trigger-type enable/disable + severity-threshold control over their in-app notification bell. Per-tenant and persisted server-side via the notification-settings API; categories the viewer\'s role cannot read render disabled with an explanation.',
    enabled: false,
  },
  {
    id: 'ai-incidents-ir-workflow',
    name: 'AI: Incidents — Response Workflow',
    description:
      'Show the NIST 800-61 Incident Response workspace (Chain of Custody callout + 5-phase Detected→Contain→Eradicate→Recover→Close workflow) on the confirmed-incident drawer Overview tab. Hidden by default. Phase progress tracks the real lifecycle status of the incident and advancing a phase transitions it; the Eradicate/Recover/Close agent actions and the evidence-package ids remain demo/mock until backend IR fields land.',
    enabled: false,
  },
  {
    id: 'ai-incidents-eradication',
    name: 'Enable incident eradication',
    description:
      'Include the Eradicate phase in the confirmed-incident IR workspace (requires "AI: Incidents — Response Workflow"). When off the phase rail runs Detected→Contain→Recover→Close and no eradication recommendations (secret invalidation, tainted-context purge, detection-gap closure) are offered.',
    enabled: false,
  },
  {
    id: 'ai-incidents-recovery',
    name: 'Enable incident recovery',
    description:
      'Include the Recover phase in the confirmed-incident IR workspace (requires "AI: Incidents \u2014 Response Workflow"). When off the phase rail skips straight from the previous phase to Close and no recovery recommendations (least-privilege re-issue, baselined-path restore, residual-access verification) are offered.',
    enabled: false,
  },
  {
    id: 'runtime-response-credential-rotation',
    name: 'Runtime Response: Credential Rotation',
    description:
      'Show credential-rotation content (recommendation cards, approval, status and timeline) inside the confirmed-incident Contain phase panel, alongside network isolation. Turning this off removes the section entirely rather than rendering an empty state — PRD FR-7.8 requires an unavailable response action be absent, never a disabled option.',
    enabled: false,
  },
  {
    id: 'global-time-filter',
    name: 'Global Time Filter',
    description:
      'Enable the global time-based filter control in the header, allowing users to scope all dashboard sections to a time window.',
    enabled: true,
  },

  // Development Features (used in settings and dev logic)
  {
    id: 'gcp-surfaces-demo',
    name: 'GCP Surfaces Demo',
    description:
      'Preview the GCP connection surfaces with fixture data, for reviewing them without a GCP project and a real service-account key.',
    enabled: false,
  },
  {
    id: 'react-query-devtools',
    name: 'ReactQuery Dev Tools',
    description:
      'Enable developer tools to interact with and visualize the TanStack/react-query cache.',
    enabled: true,
  },
  {
    id: 'development-mode',
    name: 'Development Mode',
    description:
      'Show yellow dashed borders around phase-hidden sections instead of hiding them completely',
    enabled: false,
  },
]

// Provider props type
type FeatureFlagsProviderProps = {
  children: ReactNode
  /** For testing: override which flag ids are enabled. Bypasses localStorage. */
  initialEnabledFlags?: string[]
}

/**
 * FeatureFlagsProvider Component
 *
 * Provides feature flag functionality throughout the application with localStorage
 * persistence and comprehensive MVP/Demo mode logic.
 */
export const FeatureFlagsProvider = ({
  children,
  initialEnabledFlags,
}: FeatureFlagsProviderProps): React.JSX.Element => {
  // Get phase state from AuthContext (future phases only)
  const { isPhaseFutureEnabled } = useAuth()
  // Initialize state from localStorage with error handling
  const [flags, setFlags] = useState<FeatureFlag[]>(() => {
    // Test override — skip localStorage when initialEnabledFlags is provided
    if (initialEnabledFlags !== undefined) {
      const enabledSet = new Set(initialEnabledFlags)
      return AVAILABLE_FEATURE_FLAGS.map(f => ({
        ...f,
        enabled: enabledSet.has(f.id),
      }))
    }
    if (typeof window !== 'undefined') {
      const storedFlags = localStorage.getItem('feature-flags')
      if (storedFlags) {
        try {
          const parsed = JSON.parse(storedFlags)
          // Merge with default flags to handle new flags being added
          return AVAILABLE_FEATURE_FLAGS.map(defaultFlag => {
            const storedFlag = parsed.find(
              (f: FeatureFlag) => f.id === defaultFlag.id,
            )
            return storedFlag
              ? { ...defaultFlag, enabled: storedFlag.enabled }
              : defaultFlag
          })
        } catch (error) {
          console.error('Error parsing stored feature flags:', error)
          return AVAILABLE_FEATURE_FLAGS
        }
      }
    }
    return AVAILABLE_FEATURE_FLAGS
  })

  // Persist to localStorage on state changes. Skip entirely for test overrides
  // (initialEnabledFlags) so they never overwrite the user's persisted flags.
  useEffect(() => {
    if (initialEnabledFlags !== undefined) {
      return
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem('feature-flags', JSON.stringify(flags))
    }
  }, [flags, initialEnabledFlags])

  // Basic feature checking
  const isFeatureEnabled = (flagId: string): boolean => {
    const flag = flags.find(f => f.id === flagId)
    return flag ? flag.enabled : false
  }

  // Toggle feature state
  const toggleFeature = (flagId: string) => {
    setFlags(prev =>
      prev.map(flag =>
        flag.id === flagId ? { ...flag, enabled: !flag.enabled } : flag,
      ),
    )
  }

  // Set explicit feature state
  const setFeatureEnabled = (flagId: string, enabled: boolean) => {
    setFlags(prev =>
      prev.map(flag => (flag.id === flagId ? { ...flag, enabled } : flag)),
    )
  }

  // Reset every feature flag to the baseline defined in AVAILABLE_FEATURE_FLAGS,
  // EXCEPT Development Mode — it's a session-scoped dev toggle, not a feature
  // default, so a "reset feature flags" action must not silently disable it
  // (keeps the Development Mode card and isDevelopmentMode() consistent after
  // reset). Phase Future / Disable Login are AuthContext-scoped and already
  // untouched here.
  const resetToDefaults = () => {
    setFlags(prev => {
      const devModeEnabled = prev.find(
        f => f.id === 'development-mode',
      )?.enabled
      return AVAILABLE_FEATURE_FLAGS.map(f =>
        f.id === 'development-mode' && devModeEnabled !== undefined
          ? { ...f, enabled: devModeEnabled }
          : f,
      )
    })
  }

  // Phase convenience methods (future phases only)
  const isPhaseFuture = (): boolean => isPhaseFutureEnabled

  // UI-specific methods (independent of MVP mode)
  const isMotionEnabled = (): boolean => {
    // Use direct isFeatureEnabled (not MVP-aware) to ensure UI preferences
    // work regardless of MVP/Demo mode
    // When "Essential Interface Motion Only" is enabled, motion is disabled
    return !isFeatureEnabled('essential-interface-motion-only')
  }

  const isHierarchicalOrgEnabled = (): boolean => {
    return isFeatureEnabled('org-hierarchy')
  }

  // Development mode method
  const isDevelopmentMode = (): boolean => {
    return isFeatureEnabled('development-mode')
  }

  // ReactQuery Dev Tools method with development mode dependency
  const isReactQueryDevToolsEnabled = (): boolean => {
    // ReactQuery Dev Tools is automatically disabled when Development Mode feature flag is disabled
    if (!isFeatureEnabled('development-mode')) {
      return false
    }
    return isFeatureEnabled('react-query-devtools')
  }

  // Business Impact Import/Export method
  const isBizImpactImportExport = (): boolean => {
    return isFeatureEnabled('biz-impact-import-export')
  }

  // Onboarding Wizard method
  const hasOnboardingWizard = (): boolean => {
    return isFeatureEnabled('use-onboarding-wizard')
  }

  // GCP connection onboarding on Cloud Services
  const hasGcpOnboarding = (): boolean => {
    return isFeatureEnabled('gcp-onboarding')
  }

  // Fixture-data preview of the GCP surfaces, for review without GCP
  const hasGcpSurfacesDemo = (): boolean => {
    return isFeatureEnabled('gcp-surfaces-demo')
  }

  // Financial Exposure widget on main dashboard
  const hasFinancialExposure = (): boolean => {
    return isFeatureEnabled('financial-exposure')
  }

  // Business Impact columns and BI across the app
  const hasBusinessImpact = (): boolean => {
    return isFeatureEnabled('business-impact')
  }

  // Ripple Viewer tab in Application drawer panels
  const hasRippleViewer = (): boolean => {
    return isFeatureEnabled('ripple-viewer')
  }

  // Global time-based filter control
  const hasGlobalTimeFilter = (): boolean => {
    return isFeatureEnabled('global-time-filter')
  }

  const value: FeatureFlagsContextType = {
    flags,
    isFeatureEnabled,
    toggleFeature,
    setFeatureEnabled,
    resetToDefaults,
    isPhaseFuture,
    isMotionEnabled,
    isDevelopmentMode,
    isHierarchicalOrgEnabled,
    isReactQueryDevToolsEnabled,
    isBizImpactImportExport,
    hasOnboardingWizard,
    hasGcpOnboarding,
    hasGcpSurfacesDemo,
    hasFinancialExposure,
    hasBusinessImpact,
    hasRippleViewer,
    hasGlobalTimeFilter,
  }

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

// Export types for external use
export type { FeatureFlag, FeatureFlagsContextType }
